import React from 'react';
import { Container } from '@ui';
import { Select, TextField } from '@ui/inputs';
import { useGptModels } from '@hooks/features/useGptModels';

// The `score_gen.model` picker, driven by the live model catalog.
//
// The catalog comes from `GET v2/genai/models` (see services/gptModels.js), loaded once at app
// initialization into Redux, so this is the same list every other model picker in the app shows.
//
// Two honest limits, both surfaced rather than hidden:
//
//   1. The scoring engine validates `model` against its OWN price table at lock, which is not
//      guaranteed to be identical to this catalog. A name picked here is therefore very likely
//      right, not certainly right — the hint says so instead of implying the list is authoritative.
//   2. If the catalog is empty — the fetch failed, or this is rendered outside the initialized app —
//      the control degrades to free text. A dropdown with nothing in it would make the field
//      unauthorable, which is worse than an unchecked one.
//
// A model already in the spec that the catalog does not list is added as its own option, so opening
// an older spec shows the real model rather than an empty box.

const OTHER = '__other__';

export const ModelSelect = ({ value, onChange }) => {
  const { models } = useGptModels();
  const current = value || '';

  const [wantsCustom, setWantsCustom] = React.useState(false);

  const catalogIds = models.map((model) => model.id);
  const isListed = catalogIds.indexOf(current) > -1;

  if (models.length === 0) {
    return (
      <TextField
        label="Model"
        mono
        value={current}
        onChange={onChange}
        placeholder="gpt-5-mini-2025-08-07"
        hint="The model catalog is unavailable, so this is free text. The compile validates the name."
      />
    );
  }

  const options = models
    .map((model) => ({
      value: model.id,
      label: model.display_name && model.display_name !== model.id ? `${model.display_name} — ${model.id}` : model.id,
    }))
    .concat(current && !isListed ? [{ value: current, label: `${current} — not in the catalog` }] : [])
    .concat([{ value: OTHER, label: 'Other…' }]);

  return (
    <div>
      <Select
        label="Model"
        value={wantsCustom ? OTHER : current}
        placeholder={current ? undefined : 'Choose a model…'}
        options={options}
        invalid={!!current && !isListed}
        hint={
          wantsCustom
            ? undefined
            : current && !isListed
            ? 'Not in the app’s model catalog. It may still be valid — the scoring engine keeps its own list.'
            : 'From the app’s model catalog. The scoring engine validates the name against its own price table at lock.'
        }
        onChange={(next) => {
          if (next === OTHER) {
            setWantsCustom(true);
            return;
          }
          setWantsCustom(false);
          onChange(next);
        }}
      />
      {wantsCustom && (
        <Container top={8}>
          <TextField
            aria-label="Model — custom"
            mono
            value={current}
            onChange={onChange}
            placeholder="gpt-5-mini-2025-08-07"
            hint="Not checked here. The scoring engine rejects an unpriced model at lock."
          />
        </Container>
      )}
    </div>
  );
};
